import { parseWithZod } from '@conform-to/zod'
import { type MetaFunction, type ActionFunctionArgs } from '@remix-run/node'
import { json, redirect, useLoaderData } from '@remix-run/react'
import { CarService } from '#app/entities/car/service.server.js'
import { ContactService } from '#app/entities/contact/service.server.ts'
import MasInformacionEmail from '#app/features/email/masInformacionEmail.tsx'
import { getMetaTags } from '#app/shared/lib/meta-tags.ts'
import { invariantResponse } from '#app/shared/lib/misc.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { redirectWithToast } from '#app/shared/ui/feedback/toast/toast.server.ts'
import Header from '#app/shared/ui/layout/Header.js'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { ContactTypeEnum } from '../../../../types/enums'
import CarSummary from '../me-interesa/ui/carSummary'
import ContactForm, { ContactFormSchema } from './ui/ContactForm'

export async function loader({ params }: { params: { carSlug?: string } }) {
  const carSlug = params.carSlug
  const car = await CarService.findBySlug(carSlug!)
  invariantResponse(car, 'Car not found', {
    status: 404,
  })
  return { carSlug, car }
}

export const meta: MetaFunction = ({ matches }) => {
  return getMetaTags({
    matches,
    title: 'Mas Información | Gama Óptima',
    index: false,
  })
}

export async function action({ params, request }: ActionFunctionArgs) {
  const carSlug = params.carSlug
  const car = await CarService.findBySlug(carSlug!)
  invariantResponse(car, 'Car not found', {
    status: 404,
  })

  const formData = await request.formData()
  const submission = parseWithZod(formData, {
    schema: ContactFormSchema,
  })

  if (submission.status !== 'success') {
    return json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { name, postalCode, email, fullPhone, message } = submission.value

  const contact = await ContactService.create({
    name,
    postalCode,
    email,
    phoneNumber: fullPhone,
    message: message,
    type: ContactTypeEnum.MORE_INFORMATION,
  })

  await MasInformacionEmail({
    name,
    postalCode,
    email,
    fullPhone,
    paymentType: message,
    car,
  })

  if (contact.success) {
    return redirect(`/coche/${params.carSlug}/masinformacion-confirmacion`)
  } else {
    return redirectWithToast('/', {
      title: 'Mensaje no enviado',
      description: 'Ha ocurrido un error al enviar el mensaje',
      type: 'error',
    })
  }
}

export default function Index() {
  const { car } = useLoaderData<typeof loader>()
  return (
    <>
      {/* HEADER */}
      <Header isSticky={true} showBanner={true} />
      <div
        className={`
          bg-neutral-25 px-5 py-5

          lg:pl-0
        `}
      >
        <Spacer size="36px" />
        <MainContainer
          className={`
            flex flex-col gap-8

            lg:flex-row lg:justify-between
          `}
        >
          <ContactForm />

          <CarSummary
            car={car as any}
            className={`
              rounded-none

              xl:rounded-r-base
            `}
          />
        </MainContainer>
        <Spacer size="64px" />
        <Spacer size="56px" />
      </div>
    </>
  )
}
